import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  GripVertical,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  Camera,
  MoreHorizontal,
  X
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '../../lib/utils'
import type { WorkflowStep } from '../../types/workflow'

interface StopBlockProps {
  step: WorkflowStep
  isSelected: boolean
  onSelect: () => void
  onToggle: (enabled: boolean) => void
  onDelete: () => void
  onDuplicate: () => void
  className?: string
}

export function StopBlock({
  step,
  isSelected,
  onSelect,
  onToggle,
  onDelete,
  onDuplicate,
  className
}: StopBlockProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: step.id,
    data: {
      type: 'step',
      step
    }
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const filename = (step.params as any).filename || 'final-result'

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={cn(
        "transition-all duration-200 cursor-pointer hover:shadow-md border-2 border-red-200 bg-red-50",
        isSelected && "ring-2 ring-primary ring-offset-2",
        !step.enabled && "opacity-60",
        isDragging && "opacity-50 rotate-2 shadow-lg",
        className
      )}
      onClick={onSelect}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>

          {/* Category Indicator */}
          <div className="w-3 h-3 rounded-full flex-shrink-0 bg-red-500" />

          {/* Step Icon */}
          <div className="flex-shrink-0">
            <Camera className="h-5 w-5 text-red-600" />
          </div>

          {/* Step Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-sm text-red-700">Stop</h4>
              <Badge variant="outline" className="text-xs border-red-300 text-red-700">
                Take Screenshot
              </Badge>
              {!step.enabled && (
                <Badge variant="secondary" className="text-xs">
                  Disabled
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">
              Screenshot: {filename}
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center gap-2">
              <Switch
                checked={step.enabled}
                onCheckedChange={onToggle}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Delete Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              title="Delete step"
            >
              <X className="h-3 w-3" />
            </Button>

            {/* Actions Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={(e) => { e.stopPropagation(); onToggle(!step.enabled); }}
                >
                  {step.enabled ? (
                    <>
                      <EyeOff className="h-4 w-4 mr-2" />
                      Disable
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Enable
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
